import { apiGet, handleApiError } from './apiClient';
import { Tenant } from '../models/tenant';
import { API_ENDPOINTS, buildApiUrl } from '../config/apiEndpoints';

/**
 * Singleton cache for tenant data to avoid redundant API calls
 */
let cachedTenant: Tenant | null = null;
let cachePromise: Promise<Tenant | null> | null = null;

/**
 * Fetches tenant data from the API.
 * Implements caching to avoid redundant API calls.
 *
 * @returns Promise resolving to Tenant data
 * @throws Error if the API call fails
 */
export const fetchTenant = async (useCache: boolean = true): Promise<Tenant | null> => {
  // If we have a cached version, return it immediately
  if (useCache && cachedTenant) {
    return cachedTenant;
  }

  // If a fetch is already in progress, return that promise
  if (cachePromise) {
    return cachePromise;
  }

  // Initiate the fetch
  cachePromise = (async () => {
    try {
      const response = await apiGet(buildApiUrl(API_ENDPOINTS.tenant.get));
      const data = await response.json();
      cachedTenant = data.result;
      return cachedTenant;
    } catch (error) {
      handleApiError(error, 'Failed to fetch tenant data');
      return null;
    } finally {
      cachePromise = null;
    }
  })();

  return cachePromise;
};

/**
 * Gets cached tenant data without making an API call.
 * Returns null if data hasn't been fetched yet.
 *
 * @returns Cached Tenant data or null
 */
export const getCachedTenant = (): Tenant | null => cachedTenant;

/**
 * Clears the tenant cache (useful for refresh scenarios).
 */
export const clearTenantCache = (): void => {
  cachedTenant = null;
  cachePromise = null;
};

